import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  BizFrontendApiService,
  BizFrontendSettingService,
} from '@interserve/angular';
import type { HelpMenuItem } from '../components/header/config/help-menu.config';
import { HELP_MENU_ITEMS } from '../components/header/config/help-menu.config';

/** getShearedUserSettingsForSettingCd の settingCd */
const INFORMATION_MESSAGE_SETTING_CD = 'informationMessage';

export interface InformationMessageResult {
  enable: boolean;
  messages: HelpMenuItem[];
}

/**
 * ヘッダー用「使い方・よくあるご質問・お問い合わせ」メニューを
 * ライブラリの getShearedUserSettingsForSettingCd で取得するサービス。
 * 取得失敗時は埋め込みの HELP_MENU_ITEMS を返す。
 */
@Injectable({ providedIn: 'root' })
export class InformationMessageService {
  constructor(
    private bizFrontendApiService: BizFrontendApiService,
    private bizFrontendSettingService: BizFrontendSettingService
  ) {}

  /**
   * 情報メッセージ（ヘルプメニュー）を取得する。
   * 失敗時は enable: true と埋め込みメニューを返す。
   */
  getInformationMessage(): Observable<InformationMessageResult> {
    return this.bizFrontendApiService
      .getShearedUserSettingsForSettingCd(INFORMATION_MESSAGE_SETTING_CD)
      .pipe(
        map((response) => {
          // レスポンスから data を取得（screen-message.service.ts と同じパターン）
          const payload =
            response && typeof response === 'object' && 'data' in response
              ? (response as { resultCode?: number; data?: unknown }).data
              : response;

          if (!payload || typeof payload !== 'object') {
            return { enable: true, messages: HELP_MENU_ITEMS };
          }

          const data = payload as {
            enable?: boolean;
            messages?: Array<{
              id: string;
              label: string;
              url?: string;
              enabled?: boolean;
            }>;
          };

          const raw = (data?.messages ?? []).map((m) => ({
            id: m.id,
            label: m.label,
            url: m.url ?? '',
            enabled: m.enabled ?? true,
          }));

          return {
            enable: data?.enable ?? true,
            messages: raw.length > 0 ? raw : HELP_MENU_ITEMS,
          };
        }),
        catchError(() => of({ enable: true, messages: HELP_MENU_ITEMS }))
      );
  }
}
